import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchAPI } from '../utils/api';
import Navbar from '../components/layout/Navbar';
import FloatingAction from '../components/home1/FloatingAction';
import { FaGraduationCap, FaStar, FaQuoteLeft, FaUser, FaPen } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';

export default function StudentFeedback() {
    const { currentUser } = useAuth();
    const [feedbackData, setFeedbackData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadFeedback = async () => {
            try {
                const data = await fetchAPI('/public/get_feedback.php');
                setFeedbackData(data);
            } catch (err) {
                console.error("Failed to load feedback", err);
            } finally {
                setLoading(false);
            }
        };
        loadFeedback();
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: 'spring', stiffness: 100 }
        }
    };

    return (
        <div className="bg-[#0a0a0a] min-h-screen font-sans text-white relative overflow-hidden">
            {/* Background Aesthetic Elements */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_80%)]"></div>
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[120px] pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-blue-600/5 rounded-full blur-[120px] pointer-events-none"></div>

            <Navbar />

            {/* Header Section */}
            <div className="relative pt-32 pb-16 px-6">
                <div className="max-w-7xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center justify-center gap-4 mb-4"
                    >
                        <div className="h-[1px] w-8 bg-blue-500"></div>
                        <span className="text-blue-500 font-black uppercase tracking-[0.3em] text-[10px]">Academic Records // Testimonials</span>
                        <div className="h-[1px] w-8 bg-blue-500"></div>
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-4xl md:text-6xl font-black tracking-tighter text-white uppercase italic"
                    >
                        STUDENT <span className="text-blue-500">SUCCESS</span> STORIES
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-white/40 font-medium max-w-2xl mx-auto mt-6 text-[10px] uppercase tracking-[0.3em] leading-relaxed"
                    >
                        Precision data from our academic registry. Verified student outcomes and learning journey highlights from the 2026 intake.
                    </motion.p>
                </div>
            </div>

            {/* Testimonials Grid */}
            <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="w-10 h-10 border-4 border-blue-500/10 border-t-blue-500 rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8"
                    >
                        {feedbackData.map((feedback) => (
                            <motion.div
                                key={feedback.id}
                                variants={itemVariants}
                                className="break-inside-avoid bg-white/[0.03] backdrop-blur-xl p-8 rounded-[2rem] border border-white/5 hover:border-blue-500/30 transition-all duration-500 group relative overflow-hidden"
                            >
                                {/* Decorative Quote Mark */}
                                <div className="absolute top-6 right-8 text-5xl font-serif text-white/[0.02] group-hover:text-blue-500/[0.05] transition-colors pointer-events-none">"</div>

                                {/* Header: User Info & Sentiment */}
                                <div className="flex items-center gap-4 mb-8 relative z-10">
                                    <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center text-blue-500 font-black text-xl shadow-2xl group-hover:scale-110 transition-transform">
                                        {feedback.author.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="font-black text-white text-base mb-1 uppercase italic tracking-tight">{feedback.author}</h3>
                                        <span className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-lg border ${feedback.sentiment === 'satisfied'
                                            ? 'bg-blue-500/10 border-blue-500/20 text-blue-500'
                                            : 'bg-white/5 border-white/10 text-white/40'
                                            }`}>
                                            {feedback.sentiment === 'satisfied' ? 'Verified Intake' : 'Active Student'}
                                        </span>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="relative z-10 mb-8">
                                    <h4 className="font-black text-white text-lg mb-4 leading-tight uppercase italic group-hover:text-blue-400 transition-colors">
                                        {feedback.headline}
                                    </h4>
                                    <p className="text-white/60 text-sm leading-relaxed italic">
                                        "{feedback.content}"
                                    </p>
                                </div>

                                {/* Footer: Tags & Time */}
                                <div className="pt-6 border-t border-white/5 flex flex-wrap items-center justify-between gap-4 relative z-10">
                                    <div className="flex flex-wrap gap-2">
                                        {feedback.tags && feedback.tags.slice(0, 2).map((tag, i) => (
                                            <span key={i} className="text-[9px] font-black text-blue-500/80 uppercase tracking-widest">
                                                // {tag.trim()}
                                            </span>
                                        ))}
                                    </div>
                                    <span className="text-[10px] font-black text-white/20 uppercase tracking-[0.2em] font-mono">
                                        {feedback.time}
                                    </span>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </div>

            <FloatingAction />
        </div>
    );
}
